import { useEffect, useState } from 'react'
import { DataGrid, DataGridProps, GridColDef } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { AlertPhone, Device, RuuviTag, UserAlertRule } from '../../types'
import { AlertGroup } from '../../types/Alerts'
import useDataGrid from '../../hooks/useDataGrid'
import { usePolicy } from '../../contexts/PolicyContext'
import { makeAlertRuleDataGridRows, translateAlertRuleName } from '../../utils/alert'
import AlertPhoneAlertRuleSmsNotificationsSwitch from './AlertPhoneAlertRuleSmsNotificationsSwitch'
import AlertPhoneAlertRuleEnableSwitch from './AlertPhoneAlertRuleEnableSwitch'
import AlertRuleLabel from '../AlertRuleLabel'
import { AlertRuleDataGridRow } from '../../types/AlertRuleDataGridRow'

interface AlertPhoneAlertRulesDataGridProps extends Omit<DataGridProps, 'rows' | 'columns'> {
  alertPhone: AlertPhone
  ruuviTag?: RuuviTag
  device?: Device
  alertPhoneAlertRules: UserAlertRule[]
  alertRules: AlertGroup[]
  onUpdateAlertRules?: (alertRules: AlertGroup[]) => void
  onUpdateAlertPhoneAlertRules?: (alertPhoneAlertRules: UserAlertRule[]) => void
}

export default function AlertPhoneAlertRulesDataGrid({
  alertPhone,
  ruuviTag,
  device,
  alertPhoneAlertRules,
  alertRules,
  onUpdateAlertRules: _,
  onUpdateAlertPhoneAlertRules,
  ...props
}: AlertPhoneAlertRulesDataGridProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The policy state.
   */
  const { canEditAlertPhone } = usePolicy()

  /**
   * The data grid props.
   */
  const dataGridProps = useDataGrid(props)

  /**
   * The enabled alerts as "rows".
   */
  const [rows, setRows] = useState<AlertRuleDataGridRow[]>(() => getInitialRows())

  /**
   * The columns.
   */
  const columns: GridColDef<AlertRuleDataGridRow>[] = [
    {
      field: 'name',
      headerName: t('name'),
      width: 200,
      valueGetter: (_, row) => translateAlertRuleName(row.name),
      editable: false,
      renderCell: ({ row }) => <AlertRuleLabel alertRule={row} />
    },
    {
      field: 'alertRule.smsNotificationEnabled',
      headerName: t('smsNotifications'),
      flex: 0,
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
      valueGetter: (_, row) => row?.userAlertRule?.smsNotificationEnabled ?? false,
      renderCell: ({ row }) => {
        if (canEditAlertPhone(alertPhone) && row?.userAlertRule !== undefined) {
          return (
            <AlertPhoneAlertRuleSmsNotificationsSwitch
              alertPhone={alertPhone}
              ruuviTag={ruuviTag}
              alertRule={row.userAlertRule}
              alertRules={alertPhoneAlertRules}
              afterSubmit={onUpdateAlertPhoneAlertRules}
            />
          )
        } else {
          return (
            <AlertPhoneAlertRuleEnableSwitch
              alertPhone={alertPhone}
              ruuviTag={ruuviTag}
              alertRules={alertPhoneAlertRules}
              ruleGroupName={row.group}
              alertRuleName={row.name}
              size="small"
              afterEnable={onUpdateAlertPhoneAlertRules}
            />
          )
        }
      }
    }
  ]

  /**
   * Get the intial rows from enabled alert rules.
   */
  function getInitialRows(): AlertRuleDataGridRow[] {
    return makeAlertRuleDataGridRows(alertRules, alertPhoneAlertRules, true)
  }

  /**
   * Set the rows when the alert rules or the alert phone's alert rules change.
   */
  useEffect(() => {
    setRows(getInitialRows())
  }, [alertRules, alertPhoneAlertRules])

  return (
    <DataGrid
      rows={rows}
      columns={columns}
      editMode="row"
      disableColumnMenu
      getRowHeight={() => 72}
      {...dataGridProps}
    />
  )
}
